import React, { useMemo } from "react";
import {
  Box,
  Button,
  Card,
  Fade,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { IBooking } from "../../../redux/booking/booking.types";
import { Dialog } from "../../../components/Dialogs/Dialog";
import { IOpportunities } from "../../../redux/opportunites/opportunites.types";
import { useTimezoneFormatter } from "../../../hooks/useTimezoneFormatter";

interface IBookedVenueDialogProps {
  open: boolean;
  onClose: () => void;
  isFromHoldError?: boolean;
  BookingData: IBooking;
}

export const months_list = [
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];

export const getYearsList = () => {
  const yersList = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear; i < currentYear + 10; i++) {
    yersList.push(i);
  }
  return yersList;
};

export const BookedVenueDialog: React.FC<IBookedVenueDialogProps> = (props) => {
  const { open, onClose, BookingData, isFromHoldError = false } = props;

  const { ftDate } = useTimezoneFormatter();

  const [checked, setChecked] = React.useState(false);

  const availabilityDetails = useMemo(() => {
    if (BookingData.status === "BOOKED") {
      const bookingDetails = BookingData as IBooking;
      return (
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <Typography variant="h4" fontWeight={600}>
              Venue Name
            </Typography>
            <Typography variant="body1" fontSize={"0.95rem"}>
              {bookingDetails?.venue_name || "-"}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="h4" fontWeight={600}>
              Booking ID
            </Typography>
            <Typography variant="body1" fontSize={"0.95rem"}>
              {bookingDetails?.booking_unique_id || "-"}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="h4" fontWeight={600}>
              Customer Name
            </Typography>
            <Typography variant="body1" fontSize={"0.95rem"}>
              {bookingDetails?.customer_name || "-"}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="h4" fontWeight={600}>
              Customer Email
            </Typography>
            <Typography variant="body1" fontSize={"0.95rem"}>
              {bookingDetails?.customer_email || "-"}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="h4" fontWeight={600}>
              Customer Mobile
            </Typography>
            <Typography variant="body1" fontSize={"0.95rem"}>
              {bookingDetails?.customer_mobile_no || "-"}
            </Typography>
          </Grid>

          <Grid item xs={4}>
            <Typography variant="h4" fontWeight={600}>
              Shift Time
            </Typography>
            <Typography variant="body1" fontSize={"0.95rem"}>
              {bookingDetails?.shift_time || "-"}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="h4" fontWeight={600}>
              Status
            </Typography>
            <Typography variant="body1" fontSize={"0.95rem"}>
              {bookingDetails?.status || "-"}
            </Typography>
          </Grid>

          <Grid item xs={4}>
            <Typography variant="h4" fontWeight={600}>
              Modified By
            </Typography>
            <Typography variant="body1" fontSize={"0.95rem"}>
              {bookingDetails?.modified_by_name || "-"}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="h4" fontWeight={600}>
              Date
            </Typography>
            <Typography variant="body1" fontSize={"0.95rem"}>
              {ftDate(BookingData?.from_date) || "-"}
            </Typography>
          </Grid>
        </Grid>
      );
    }

    if (BookingData.status === "TENTATIVE_HOLD") {
      const bookingDetails = BookingData as any as IOpportunities;
      return (
        <>
          {bookingDetails.hold_details &&
          bookingDetails.hold_details.length > 0 ? (
            bookingDetails.hold_details.map((holding_detail, index) => (
              <Card key={index} variant="outlined" sx={{ mb: 2, p: 2 }}>
                <Grid container spacing={3}>
                  <Grid item xs={4}>
                    <Typography variant="h4" fontWeight={600}>
                      Start Date
                    </Typography>
                    <Typography variant="body1" fontSize={"0.95rem"}>
                      {ftDate(BookingData?.from_date) || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="h4" fontWeight={600}>
                      End Date
                    </Typography>
                    <Typography variant="body1" fontSize={"0.95rem"}>
                      {ftDate(BookingData?.to_date) || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="h4" fontWeight={600}>
                      Shift Time
                    </Typography>
                    <Typography variant="body1" fontSize={"0.95rem"}>
                      {holding_detail?.end_date || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="h4" fontWeight={600}>
                      Event
                    </Typography>
                    <Typography variant="body1" fontSize={"0.95rem"}>
                      {holding_detail?.event_name || "-"}
                    </Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography variant="h4" fontWeight={600}>
                      Reason for Hold
                    </Typography>
                    <Typography variant="body1" fontSize={"0.95rem"}>
                      {holding_detail?.reason_for_hold || "-"}
                    </Typography>
                  </Grid>
                </Grid>
              </Card>
            ))
          ) : (
            <Typography
              variant="body1"
              textAlign={"center"}
              fontSize={"0.95rem"}
            >
              No hold details available.
            </Typography>
          )}
        </>
      );
    }

    return <></>;
  }, [BookingData]);

  const bookingMessage =
    BookingData.status === "BOOKED"
      ? "This venue is already confirmed for the selected date. Please choose a different date."
      : BookingData.status === "TENTATIVE_HOLD"
        ? "This venue is temporarily reserved for the selected date. Please select another date."
        : BookingData.status === "HOLD"
          ? "This venue is currently on hold for the selected date. Availability is limited—please choose another date."
          : "This venue is unavailable for the selected date. Please select a different date.";
  return (
    <Dialog
      open={open}
      title={"Booking Details"}
      onClose={onClose}
      size="md"
      actions={[
        {
          type: "button",
          label: "Close",
          variant: "text",
          onClick: onClose,
        },
      ]}
    >
      <Typography
        variant="body1"
        textAlign={"center"}
        fontWeight={600}
        fontSize={"1.2rem"}
        mt={3}
        mb={2}
      >
        {bookingMessage}
      </Typography>
      {BookingData.status === "BOOKED" && (
        <Stack direction={"row"} justifyContent={"center"} mb={3}>
          <Button variant="contained" onClick={() => setChecked(!checked)}>
            {!checked ? `View more details` : "Hide details"}
          </Button>
        </Stack>
      )}

      {checked && (
        <Fade in={checked}>
          <Box>{availabilityDetails}</Box>
        </Fade>
      )}
    </Dialog>
  );
};
